<script setup lang="ts">
  import type { CheckboxValueType } from "ant-design-vue/es/checkbox/interface";
  import { onMounted, reactive, ref, watch } from "vue";
  import { recruitmentData } from "~/assets/data/recruitment";
  import Logger from "~/utils/logger";

  // 在Nuxt 3中，使用definePageMeta来设置布局
  definePageMeta({
    layout: "nav-layout",
  });

  const jobPlainOptions: string[] = ["市场/运营", "产品", "研发"];
  let jobDefaultCheckedList: string[] = [];
  const jobTypePlainOptions: string[] = ["社招", "校招 (实习生)"];
  let jobTypeDefaultCheckedList: string[] = [];
  const adressPlainOptions: string[] = ["深圳"];
  let adressDefaultCheckedList: string[] = ["深圳"];

  interface StateCategory {
    checkedList: string[];
    indeterminate: boolean;
    checkAll: boolean;
    jobPlainOptions: string[];
  }

  interface State {
    jobCategory: StateCategory;
    jobType: Omit<StateCategory, "jobPlainOptions"> & { jobTypePlainOptions: string[] };
    adress: Omit<StateCategory, "jobPlainOptions"> & { adressPlainOptions: string[] };
  }

  const api = useApi();

  const state = reactive<State>({
    // 职业类型
    jobCategory: {
      checkedList: jobDefaultCheckedList,
      indeterminate: true,
      checkAll: false,
      jobPlainOptions,
    },
    // 岗位类型
    jobType: {
      checkedList: jobTypeDefaultCheckedList,
      indeterminate: true,
      checkAll: false,
      jobTypePlainOptions,
    },
    // 城市
    adress: {
      checkedList: adressDefaultCheckedList,
      indeterminate: true,
      checkAll: false,
      adressPlainOptions,
    },
  });

  interface TransformedData {
    jobPositionType: number | undefined;
    jobType: number | undefined;
    city: number | undefined;
  }

  const tableListData = ref<any>([]);
  const dataTransformation = (): TransformedData => {
    let jobPositionType;
    let jobType;
    let city;
    switch (state.jobType?.checkedList[0]) {
      case "社招":
        jobPositionType = 0;
        break;
      case "校招 (实习生)":
        jobPositionType = 1;
        break;
    }
    switch (state.jobCategory?.checkedList[0]) {
      case "研发":
        jobType = 0;
        break;
      case "产品":
        jobType = 1;
        break;
      case "市场/运营":
        jobType = 2;
        break;
    }
    if (state.adress?.checkedList[0]) {
      city = 0;
    }
    return {
      jobPositionType,
      jobType,
      city,
    };
  };

  interface RecruitmentItem {
    jobPositionType: number;
    jobType: number;
  }

  const filterRecruitmentData = () => {
    const { jobPositionType, jobType, city } = dataTransformation();
    if (city !== undefined) {
      let filterPositionType = recruitmentData;
      if (jobPositionType !== undefined) {
        filterPositionType = recruitmentData.filter((item: RecruitmentItem) => {
          return jobPositionType === item.jobPositionType;
        });
      }
      let filterJobType = filterPositionType;
      if (jobType !== undefined) {
        filterJobType = filterPositionType.filter((item: RecruitmentItem) => {
          return jobType === item.jobType;
        });
      }
      tableListData.value = filterJobType;
      return;
    }
    tableListData.value = [];
  };

  watch(
    () => state.jobCategory.checkedList,
    () => {
      filterRecruitmentData();
    },
    {
      immediate: true,
    },
  );

  watch(
    () => state.jobType.checkedList,
    () => {
      filterRecruitmentData();
    },
    {
      immediate: true,
    },
  );

  watch(
    () => state.adress.checkedList,
    () => {
      filterRecruitmentData();
    },
    {
      immediate: true,
    },
  );

  const categoryOnChange = (checkedValues: CheckboxValueType[]) => {
    // 限制最多只能选中一个
    if (checkedValues.length > 1) {
      const index = checkedValues.findIndex((item: CheckboxValueType) => {
        return item !== jobDefaultCheckedList[0];
      });
      jobDefaultCheckedList = checkedValues.slice(index, index + 1) as string[];
      state.jobCategory.checkedList = jobDefaultCheckedList; // 只保留最后一个选中的值
    } else {
      jobDefaultCheckedList = checkedValues as string[];
      state.jobCategory.checkedList = checkedValues as string[];
    }
  };

  const jobTypeOnChange = (checkedValues: CheckboxValueType[]) => {
    // 限制最多只能选中一个
    if (checkedValues.length > 1) {
      const index = checkedValues.findIndex((item: CheckboxValueType) => {
        return item !== jobTypeDefaultCheckedList[0];
      });
      jobTypeDefaultCheckedList = checkedValues.slice(index, index + 1) as string[];
      state.jobType.checkedList = jobTypeDefaultCheckedList; // 只保留最后一个选中的值
    } else {
      jobTypeDefaultCheckedList = checkedValues as string[];
      state.jobType.checkedList = checkedValues as string[];
    }
  };

  const adressOnChange = (checkedValues: CheckboxValueType[]) => {
    if (checkedValues.length === 1) {
      adressDefaultCheckedList = checkedValues as string[];
      state.adress.checkedList = checkedValues as string[];
    } else {
      adressDefaultCheckedList = checkedValues as string[];
      state.adress.checkedList = checkedValues as string[];
    }
  };

  interface RecruitmentDetail {
    title?: string;
    jobResponsibilities?: string;
    jobRequirements?: string;
    applicationMethod?: string;
    jobBenefits?: string;
    tip?: string;
    sources?: string;
  }
  // 招聘详情
  const isShowDetail = ref<boolean>(false);
  const recruitmentDetail = ref<RecruitmentDetail>({});
  const toDetail = (item: RecruitmentDetail) => {
    Logger.event("招聘岗位_点击");
    isShowDetail.value = true;
    recruitmentDetail.value = item;
  };

  const reset = () => {
    jobDefaultCheckedList = [];
    jobTypeDefaultCheckedList = [];
    adressDefaultCheckedList = ["深圳"];
    state.jobType.checkedList = jobTypeDefaultCheckedList;
    state.jobCategory.checkedList = jobDefaultCheckedList;
    state.adress.checkedList = adressDefaultCheckedList;
    recruitmentDetail.value = {};
  };

  // 特殊埋点
  const postSpecialTrack = () => {
    api.ai.specialTrack({ referer: window.location.href });
  };

  onMounted(() => {
    Logger.event("人才招聘_浏览");
    postSpecialTrack();
  });
</script>

<template>
  <div class="recruitment-container tw-w-full">
    <div class="recruitment-content tw-w-full">
      <div class="banner tw-pb-[20px] tw-pt-[56px]">
        <div class="tw-flex tw-w-full tw-items-center tw-justify-center">
          <div>
            <div class="tw-max-h-[75px] tw-max-w-[796px]">
              <img class="tw-size-full tw-object-cover" src="~/assets/images/recruitment/email.png" />
            </div>
            <h2
              class="tw-mb-0 tw-mt-[6px] tw-text-[14px] tw-text-[#ffffff] sm:tw-text-[16px] md:tw-text-[18px] lg:tw-text-[20px]
                xl:tw-text-[25px]">
              加入我们，携手开启法律科技新篇章！
            </h2>
          </div>
          <div class="tw-max-h-[261px] tw-max-w-[246px] md:tw-ml-[200px]">
            <img class="tw-size-full tw-object-cover" src="~/assets/images/recruitment/recruitment-logo.png" />
          </div>
        </div>
      </div>
      <div class="recruitment-list tw-w-full">
        <div v-if="!isShowDetail" class="tw-w-full tw-pl-[28px] tw-pr-[64px] tw-pt-[24px]">
          <div class="tw-flex">
            <div class="job-filter tw-sticky tw-top-0 tw-h-[592px] tw-w-[256px] tw-pb-[50px]">
              <div class="job-filter-content tw-size-full tw-rounded-[12px] tw-bg-[#5b5cd9] tw-p-[32px]">
                <div class="filter-tag tw-flex tw-items-center tw-justify-between tw-text-white">
                  <div>筛选</div>
                  <div class="tw-cursor-pointer tw-text-[12px]" @click="reset">
                    <span class="reset-btn tw-px-[14px] tw-py-[4px]">重置</span>
                  </div>
                </div>
                <div class="items-list tw-mt-[23px]">
                  <div class="job-filter-item tw-p-[12px] tw-text-white">
                    <div class="job-category">
                      <div>职业类别</div>
                    </div>
                    <a-checkbox-group
                      v-model:value="state.jobCategory.checkedList"
                      :options="jobPlainOptions"
                      @change="categoryOnChange" />
                  </div>
                  <div class="job-type-item tw-my-[16px] tw-p-[12px] tw-text-white">
                    <div class="type-category">
                      <div>岗位类型</div>
                    </div>
                    <a-checkbox-group
                      v-model:value="state.jobType.checkedList"
                      :options="jobTypePlainOptions"
                      @change="jobTypeOnChange" />
                  </div>
                  <div class="job-type-item tw-p-[12px] tw-text-white">
                    <div class="type-category">
                      <div>城市</div>
                    </div>
                    <a-checkbox-group
                      v-model:value="state.adress.checkedList"
                      :options="adressPlainOptions"
                      @change="adressOnChange" />
                  </div>
                </div>
              </div>
            </div>
            <div class="job-table tw-ml-[56px] tw-min-h-[660px] tw-flex-1 tw-pb-[100px] tw-pt-[24px]">
              <div v-if="tableListData.length !== 0" class="has-data">
                <div class="title-sm tw-text-[#7a869a]">
                  <span>开启新的工作</span>
                  <span>{{ `(${tableListData.length})` }}</span>
                </div>
                <div v-for="item in tableListData" :key="item.title" class="job-table-item tw-mt-[12px]">
                  <div class="job-title tw-cursor-pointer tw-text-[22px] tw-text-[#1d2129]" @click="toDetail(item)">
                    {{ item.title }}
                  </div>
                  <div class="tw-mb-[10px] tw-mt-[8px] tw-text-[14px] tw-text-[#7a869a]">
                    <span>{{ item.sources }}</span>
                    <span>&nbsp;|&nbsp;</span>
                    <span>{{ state.adress?.checkedList[0] }}</span>
                    <span>&nbsp;|&nbsp;</span>
                    <span>{{ item.tip }}</span>
                  </div>
                  <div
                    class="job-detail tw-max-w-[1475px] tw-cursor-pointer tw-whitespace-pre-line tw-bg-[#f8f8f8] tw-px-[16px] tw-pb-[16px]
                      tw-leading-[25px] tw-text-[#556474]"
                    @click="toDetail(item)">
                    {{ item.jobRequirements }}
                  </div>
                </div>
              </div>
              <div v-else class="no-data tw-size-full">
                <div class="tw-flex tw-size-full tw-flex-col tw-items-center tw-justify-center">
                  <div class="tw-max-h-[240px] tw-max-w-[240px]">
                    <img
                      class="tw-size-full tw-object-cover"
                      src="~/assets/images/recruitment/recruitment-no-data.png" />
                  </div>
                  <div class="tw-text-[14px] tw-text-[#7a869a]">暂无职位，请尝试其他搜索条件</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="tw-pb-[96px] tw-pt-[48px]">
          <div class="tw-px-[200px]">
            <div class="job-table-item tw-mt-[12px] tw-max-w-[1475px]">
              <div class="tw-flex tw-justify-between">
                <div class="job-title tw-text-[22px] tw-text-[#1d2129]">{{ recruitmentDetail.title }}</div>
                <div
                  class="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-text-[18px]"
                  @click="isShowDetail = false">
                  <svg
                    viewBox="64 64 896 896"
                    width="1em"
                    height="1em"
                    fill="currentColor"
                    aria-hidden="true"
                    focusable="false">
                    <path
                      d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 0 0 0 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" />
                  </svg>
                  <span>返回筛选</span>
                </div>
              </div>
              <div class="tw-mb-[10px] tw-mt-[8px] tw-text-[14px] tw-text-[#7a869a]">
                <span>{{ recruitmentDetail.sources }}</span>
                <span>&nbsp;|&nbsp;</span>
                <span>{{ state.adress?.checkedList[0] }}</span>
                <span>&nbsp;|&nbsp;</span>
                <span>{{ recruitmentDetail.tip }}</span>
              </div>
              <div class="tw-mt-[10px]">
                <div class="tw-text-[20px] tw-text-[#1d2129]">岗位职责</div>
                <div
                  class="tw-mt-[16px] tw-whitespace-pre-line tw-bg-[#f8f8f8] tw-px-[16px] tw-pb-[16px] tw-leading-[25px] tw-text-[#556474]">
                  {{ recruitmentDetail.jobResponsibilities }}
                </div>
              </div>
              <div class="tw-mt-[32px]">
                <div class="tw-text-[20px] tw-text-[#1d2129]">岗位要求</div>
                <div
                  class="tw-mt-[16px] tw-whitespace-pre-line tw-bg-[#f8f8f8] tw-px-[16px] tw-pb-[16px] tw-leading-[25px] tw-text-[#556474]">
                  {{ recruitmentDetail.jobRequirements }}
                </div>
              </div>
              <div class="tw-mt-[32px]">
                <div class="tw-text-[20px] tw-text-[#1d2129]">福利待遇</div>
                <div
                  class="tw-mt-[16px] tw-whitespace-pre-line tw-bg-[#f8f8f8] tw-px-[16px] tw-pb-[16px] tw-leading-[25px] tw-text-[#556474]">
                  {{ recruitmentDetail.jobBenefits }}
                </div>
              </div>
              <div class="tw-mt-[32px]">
                <div class="tw-text-[20px] tw-text-[#1d2129]">投递方式</div>
                <div
                  class="tw-mt-[16px] tw-whitespace-pre-line tw-bg-[#f8f8f8] tw-px-[16px] tw-pb-[16px] tw-leading-[25px] tw-text-[#556474]">
                  {{ recruitmentDetail.applicationMethod }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
  .recruitment-container {
    overflow-wrap: break-word;

    .recruitment-content {
      .banner {
        width: 100%;
        background-image: url("~/assets/images/recruitment/recruitment-bg.png");
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;
      }

      .recruitment-list {
        .job-filter {
          .reset-btn {
            background: rgba(255, 255, 255, 20%);
            border: 1px solid rgba(255, 255, 255, 60%);
            border-radius: 4px;
          }

          .items-list {
            background: rgba(255, 255, 255, 13%);
            border-radius: 2px;

            .ant-checkbox-group {
              display: flex;
              flex-direction: column;

              :deep(.ant-checkbox-wrapper) {
                margin-top: 12px;
                color: #fff;

                .ant-checkbox-checked {
                  &::after {
                    border-color: #fff;
                  }

                  .ant-checkbox-inner {
                    background-color: #fff;
                    border-color: #fff;

                    &::after {
                      border-color: #4285f4;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
</style>
